import { jsPDF } from 'jspdf'

async function toDataUrl(url) {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (e) {
    console.warn('Error loading image:', e)
    return null
  }
}

function fmt(n) {
  return Number(n || 0).toFixed(2)
}

function toASCII(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, '')
}

/**
 * Generate a modern thermal ticket PDF (58mm)
 */
export async function generateModernTicket({
  logoUrl = '/img/sushi.png',
  title = 'Tranquero',
  saleId,
  items = [],
  subtotal = 0,
  discount = 0,
  tax = 0,
  total = 0,
  paymentMethod = 'cash',
  customerName = null,
  date = new Date().toLocaleString(),
  status = 'success'
}) {
  // 58mm width, initial height 200mm (will be adjusted)
  const doc = new jsPDF('p', 'mm', [58, 200])
  doc.setFont('helvetica', 'normal')

  const marginL = 4
  const marginR = 54
  const contentWidth = marginR - marginL

  // Helper to wrap text
  function wrapByWidth(text, maxWidth, size) {
    doc.setFontSize(size)
    const words = toASCII(text).split(/\s+/).filter(Boolean)
    const lines = []
    let line = ''
    for (const w of words) {
      const test = line ? `${line} ${w}` : w
      const width = doc.getTextWidth(test)
      if (width <= maxWidth) {
        line = test
      } else {
        if (line) lines.push(line)
        line = w
      }
    }
    if (line) lines.push(line)
    return lines.length ? lines : ['']
  }

  function renderItemTextOnly(doc, it, marginL, marginR, contentWidth, startY) {
    let currentY = startY;
    // Name
    doc.setFont('helvetica', 'bold')
    const nameLines = wrapByWidth(it.name || 'Producto', contentWidth, 9)
    nameLines.forEach(line => {
      doc.text(toASCII(line), marginL, currentY)
      currentY += 4
    })

    // Qty x Price = Total
    doc.setFont('helvetica', 'normal')
    const qty = Number(it.qty || it.quantity || 1)
    const price = Number(it.unit_price || 0)
    const totalItem = Number(it.total || (qty * price))

    doc.text(`${qty} x $${fmt(price)}`, marginL, currentY)
    doc.text(`$${fmt(totalItem)}`, marginR, currentY, { align: 'right' })
  }

  // Calculate required height
  // Header approx 60mm + items + totals + footer
  let rowsH = 0
  items.forEach(it => {
    // Item name lines
    const nameLines = wrapByWidth(it.name || 'Producto', contentWidth, 9)
    rowsH += (nameLines.length * 4) + 8 // Name + Price/Qty line + spacing
  })

  const baseH = 80 + rowsH + 40 + (customerName ? 10 : 0)
  const height = Math.max(100, baseH)

  // Resize page
  doc.internal.pageSize.height = height

  let y = 5

  // === GRADIENT BAR REMOVED ===
  y += 5

  // === LOGO ===
  if (logoUrl) {
    const img = await toDataUrl(logoUrl)
    if (img) {
      const logoW = 30
      const logoH = 10
      doc.addImage(img, 'PNG', (58 - logoW) / 2, y, logoW, logoH)
      y += 12
    }
  }

  // === HEADER ===
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text(toASCII(title), 29, y, { align: 'center' })
  y += 5

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`Ticket #${saleId ?? 'N/A'}`, 29, y, { align: 'center' })
  y += 4
  doc.text(toASCII(date), 29, y, { align: 'center' })
  y += 6

  // === CUSTOMER ===
  if (customerName) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.text('Cliente:', marginL, y)
    doc.setFont('helvetica', 'normal')
    const customerLines = wrapByWidth(customerName, contentWidth - 15, 9)
    customerLines.forEach(line => {
      doc.text(toASCII(line), marginL + 12, y)
      y += 4
    })
    y += 2
  }

  // Divider
  doc.setDrawColor(200, 200, 200)
  doc.line(marginL, y, marginR, y)
  y += 4

  // === ITEMS ===
  doc.setFontSize(9)
  items.forEach((it) => {
    // Product Image
    if (it.image) {
      try {
        const imgData = it.image; // Assuming base64 string
        const imgType = (it.mime || 'image/jpeg').split('/')[1].toUpperCase();
        // Add image (small thumbnail)
        doc.addImage(imgData, imgType, marginL, y, 10, 10);
        // Adjust text position to be next to image
        const textX = marginL + 12;

        // Name
        doc.setFont('helvetica', 'bold')
        const nameLines = wrapByWidth(it.name || 'Producto', contentWidth - 12, 9)
        nameLines.forEach(line => {
          doc.text(toASCII(line), textX, y + 3)
          y += 4
        })

        // Qty x Price = Total
        doc.setFont('helvetica', 'normal')
        const qty = Number(it.qty || it.quantity || 1)
        const price = Number(it.unit_price || 0)
        const totalItem = Number(it.total || (qty * price))

        doc.text(`${qty} x $${fmt(price)}`, textX, y + 4)
        doc.text(`$${fmt(totalItem)}`, marginR, y + 4, { align: 'right' })

        // Ensure y moves down enough for the image height if text is short
        y = Math.max(y + 6, y + (10 - (nameLines.length * 4)) + 4);
        y += 2; // Extra spacing
      } catch (e) {
        console.warn('Error adding product image:', e);
        // Fallback to text only if image fails
        renderItemTextOnly(doc, it, marginL, marginR, contentWidth, y);
        y += (wrapByWidth(it.name || 'Producto', contentWidth, 9).length * 4) + 10;
      }
    } else {
      // No image, render text only
      renderItemTextOnly(doc, it, marginL, marginR, contentWidth, y);
      // Calculate height increase
      const nameLines = wrapByWidth(it.name || 'Producto', contentWidth, 9)
      y += (nameLines.length * 4) + 6 + 4; // Name + Price line + spacing
    }
  })

  // Divider
  doc.line(marginL, y, marginR, y)
  y += 5

  // === TOTALS ===
  doc.setFontSize(9)

  // Subtotal
  doc.text('Subtotal:', marginL, y)
  doc.text(`$${fmt(subtotal)}`, marginR, y, { align: 'right' })
  y += 5

  if (discount > 0) {
    doc.text('Descuento:', marginL, y)
    doc.text(`-$${fmt(discount)}`, marginR, y, { align: 'right' })
    y += 5
  }

  if (tax > 0) {
    doc.text('Impuestos:', marginL, y)
    doc.text(`$${fmt(tax)}`, marginR, y, { align: 'right' })
    y += 5
  }

  // Total
  y += 2
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('TOTAL:', marginL, y)
  doc.text(`$${fmt(total)}`, marginR, y, { align: 'right' })
  y += 8

  // === PAYMENT ===
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  const paymentText = paymentMethod === 'card' ? 'Tarjeta' : 'Efectivo'
  doc.text(`Pago: ${paymentText}`, marginL, y)
  y += 5

  const statusText = status === 'canceled' ? 'Cancelado' : (status === 'refunded' ? 'Reembolsado' : 'Compra exitosa')
  if (status === 'canceled') doc.setTextColor(239, 68, 68)
  else if (status === 'refunded') doc.setTextColor(20, 184, 166)
  else doc.setTextColor(34, 197, 94)
  doc.text(`Estado: ${statusText}`, marginL, y)
  doc.setTextColor(0, 0, 0)
  y += 8

  // === FOOTER ===
  doc.setFontSize(8)
  doc.text('¡Gracias por su compra!', 29, y, { align: 'center' })
  y += 4
  doc.setTextColor(100, 100, 100)
  doc.text('Tranquero App', 29, y, { align: 'center' })

  // Save
  doc.save(`ticket-${saleId ?? Date.now()}.pdf`)
}
